<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import EChart from '../../../components/EChart.vue'
import { categoryAxis, chartBase, chartTheme, valueAxis } from '../../../components/chart-theme.js'
import { useAdminTheme } from '../../../composables/useAdminTheme.js'
import { formatInt, formatMoney } from '../llm-format.js'

const props = defineProps({
  dimension: { type: String, required: true },
  items: { type: Array, required: true },
})
const emit = defineEmits(['select'])
const { t } = useI18n()
const { theme: themeName } = useAdminTheme()
const rows = computed(() => [...props.items].reverse())

const option = computed(() => {
  const theme = chartTheme(themeName.value)
  return {
    ...chartBase(theme),
    grid: { top: 8, left: 8, right: 72, bottom: 8, containLabel: true },
    xAxis: { ...valueAxis(theme), axisLabel: { color: theme.textMuted, formatter: formatMoney } },
    yAxis: { ...categoryAxis(theme), data: rows.value.map(labelOf) },
    tooltip: { ...chartBase(theme).tooltip, formatter: tooltipOf },
    series: [{
      type: 'bar',
      name: t('visualGameAdmin.llm.cost.metric.supplier_cost_micros'),
      barMaxWidth: 22,
      itemStyle: { color: theme.bar, borderRadius: [0, 4, 4, 0] },
      label: { show: true, position: 'right', color: theme.textMuted, formatter: item => formatMoney(item.value) },
      data: rows.value.map(item => item.supplier_cost_micros),
    }],
  }
})

function labelOf(item) {
  switch (props.dimension) {
    case 'capability':
      return t(`visualGameAdmin.llm.calls.capability.${item.capability}`)
    case 'billing':
      return t(`visualGameAdmin.llm.calls.billingMode.${item.billing_mode}`)
    default:
      throw new Error(`Unhandled cost dimension: ${props.dimension}`)
  }
}

function tooltipOf(params) {
  const item = rows.value[params[0].dataIndex]
  return [
    `<strong>${labelOf(item)}</strong>`,
    `${t('visualGameAdmin.llm.cost.metric.supplier_cost_micros')}: ${formatMoney(item.supplier_cost_micros)}`,
    `${t('visualGameAdmin.llm.cost.metric.calls')}: ${formatInt(item.calls)}`,
    `${t('visualGameAdmin.llm.cost.metric.charged_points')}: ${formatInt(item.charged_points)}`,
    t('visualGameAdmin.llm.cost.chart.clickToCalls'),
  ].join('<br/>')
}

function onSelect({ index }) {
  emit('select', rows.value[index])
}
</script>

<template>
  <EChart
    :option="option"
    :height="`${Math.max(200, rows.length * 44 + 40)}px`"
    @select="onSelect"
  />
</template>
